/* Vendored shadcn/ui. Edits limited to token/density wiring (111 §2).
 *
 * `110-design-system.md` §6: "One primitive, four uses" — the `Cmd+K` palette, the `[[`
 * wiki-link menu, the `/` slash menu and the AI page picker are all this component.
 *
 * `cmdk` gives us the entire keyboard contract for free (↑/↓ move, Enter activates, type to
 * filter, the active item scrolls into view) **as long as we do not fight it**: do not
 * intercept ArrowUp/ArrowDown, and do not manage the selection by hand. Criterion 5 is
 * "zero mouse events end to end", and it is `cmdk` that earns it.
 */
import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

export function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      className={cn(
        'flex h-full w-full flex-col overflow-hidden rounded-lg bg-[var(--popover)] text-[var(--popover-foreground)]',
        className,
      )}
      {...props}
    />
  );
}

export interface CommandDialogProps extends React.ComponentProps<typeof Dialog> {
  /** Announced to screen readers; the palette shows no visible heading (density). */
  label: string;
  children: React.ReactNode;
}

export function CommandDialog({ label, children, ...props }: CommandDialogProps) {
  return (
    <Dialog {...props}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content
          // Top-anchored, not centred: the palette is a launcher, and a launcher that
          // jumps vertically as its result list grows is disorienting.
          className={cn(
            'fixed top-[15vh] left-1/2 z-50 w-full max-w-xl -translate-x-1/2 overflow-hidden',
            'rounded-lg border border-[var(--border)] bg-[var(--popover)] shadow-lg',
          )}
          aria-label={label}
        >
          <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search pages and tags, or type &gt; for commands.
          </DialogPrimitive.Description>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
}

export function CommandInput({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input>) {
  return (
    <div className="flex items-center gap-2 border-b border-[var(--border)] px-3">
      <Search
        className="size-[var(--icon)] shrink-0 text-[var(--muted-foreground)]"
        strokeWidth={1.75}
        aria-hidden
      />
      <CommandPrimitive.Input
        className={cn(
          'flex h-10 w-full bg-transparent py-3 outline-none',
          'placeholder:text-[var(--muted-foreground)] disabled:opacity-50',
          className,
        )}
        {...props}
      />
    </div>
  );
}

export function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      className={cn('max-h-[min(60vh,22rem)] overflow-x-hidden overflow-y-auto', className)}
      {...props}
    />
  );
}

export function CommandEmpty(props: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      className="px-3 py-4 text-center text-[var(--muted-foreground)]"
      {...props}
    />
  );
}

export function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      className={cn(
        'overflow-hidden p-1 text-[var(--foreground)]',
        '[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1',
        '[&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-medium',
        '[&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:uppercase',
        '[&_[cmdk-group-heading]]:text-[var(--muted-foreground)]',
        className,
      )}
      {...props}
    />
  );
}

export function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      className={cn('-mx-1 h-px bg-[var(--border)]', className)}
      {...props}
    />
  );
}

export function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      className={cn(
        'flex h-[var(--row-h)] cursor-default items-center gap-2 rounded-sm px-2',
        // `data-selected` is cmdk's KEYBOARD selection. Styling hover identically would
        // make a stray mouse position fight the arrow keys for what looks active.
        'data-[selected=true]:bg-[var(--accent)] data-[selected=true]:text-[var(--accent-foreground)]',
        'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
        '[&_svg]:size-[var(--icon)] [&_svg]:shrink-0',
        className,
      )}
      {...props}
    />
  );
}

/** The right-hand hint on a row: a shortcut, or a breadcrumb. Muted, and never truncated away. */
export function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      className={cn('ml-auto shrink-0 pl-2 text-2xs text-[var(--muted-foreground)]', className)}
      {...props}
    />
  );
}
